import { offlineStorage, OfflineVisit } from './offline-storage'
import { visitService } from '@/services/visit.service'
import { useAuthStore } from '@/stores/auth.store'

type SyncStatus = 'idle' | 'offline' | 'syncing' | 'success' | 'partial' | 'error'

class SyncManager {
  private isSyncing = false
  private syncListeners: Array<(status: SyncStatus) => void> = []

  onSyncStatusChange(callback: (status: SyncStatus) => void) {
    this.syncListeners.push(callback)
    return () => {
      this.syncListeners = this.syncListeners.filter((cb) => cb !== callback)
    }
  }

  private notifyListeners(status: SyncStatus) {
    this.syncListeners.forEach((cb) => cb(status))
  }

  private getToken(): string | null {
    return useAuthStore.getState().token
  }

  async sync(): Promise<{ success: boolean; synced: number; failed: number }> {
    if (this.isSyncing) {
      return { success: false, synced: 0, failed: 0 }
    }

    if (!navigator.onLine) {
      this.notifyListeners('offline')
      return { success: false, synced: 0, failed: 0 }
    }

    const token = this.getToken()
    if (!token) {
      return { success: false, synced: 0, failed: 0 }
    }

    this.isSyncing = true
    this.notifyListeners('syncing')

    let synced = 0
    let failed = 0

    try {
      // Sync visits
      const unsyncedVisits = await offlineStorage.getUnsyncedVisits()

      for (const visit of unsyncedVisits) {
        try {
          if (visit.isCompleted) {
            await visitService.complete(visit.serverVisitId || visit.id, {
              endLatitude: visit.endLatitude!,
              endLongitude: visit.endLongitude!,
              notes: visit.notes,
              outcome: visit.outcome as any,
              photoUrls: visit.photoUrls,
            }, token)

            await offlineStorage.deleteVisit(visit.id)
            synced++
          } else {
            const result: any = await visitService.create({
              businessId: visit.businessId,
              startLatitude: visit.startLatitude,
              startLongitude: visit.startLongitude,
            }, token)

            // Store server ID for later completion sync
            visit.serverVisitId = result.visit?.id
            visit.synced = true
            await offlineStorage.saveVisit(visit)
            synced++
          }
        } catch (error) {
          console.error('Failed to sync visit:', visit.id, error)
          failed++
        }
      }

      // Sync photos
      const unsyncedPhotos = await offlineStorage.getUnsyncedPhotos()

      for (const photo of unsyncedPhotos) {
        try {
          const { uploadUrl } = await visitService.getPhotoPresignedUrl({
            visitId: photo.visitId,
            fileName: photo.fileName,
            contentType: photo.blob.type,
          }, token) as any

          await fetch(uploadUrl, {
            method: 'PUT',
            body: photo.blob,
            headers: { 'Content-Type': photo.blob.type },
          })

          await offlineStorage.deletePhoto(photo.id)
          synced++
        } catch (error) {
          console.error('Failed to sync photo:', photo.id, error)
          failed++
        }
      }

      this.notifyListeners(failed === 0 ? 'success' : 'partial')
      return { success: failed === 0, synced, failed }
    } catch (error) {
      console.error('Sync error:', error)
      this.notifyListeners('error')
      return { success: false, synced, failed }
    } finally {
      this.isSyncing = false
    }
  }

  async hasPendingSync(): Promise<boolean> {
    const visits = await offlineStorage.getUnsyncedVisits()
    const photos = await offlineStorage.getUnsyncedPhotos()
    return visits.length > 0 || photos.length > 0
  }

  async getPendingCounts(): Promise<{ visits: number; photos: number }> {
    const visits = await offlineStorage.getUnsyncedVisits()
    const photos = await offlineStorage.getUnsyncedPhotos()
    return { visits: visits.length, photos: photos.length }
  }

  startAutoSync() {
    window.addEventListener('online', () => {
      this.sync()
    })
  }
}

export const syncManager = new SyncManager()
export type { SyncStatus }
